import React, { Suspense, useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import Button, { IconButton } from '@atlaskit/button/new';
import { SplitButton } from '@atlaskit/button/split-button';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { useFlags } from '@atlaskit/flag';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import { fg } from '@atlaskit/platform-feature-flags';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import { WorkspacePermissionsProvider } from '../../../../controllers/WorkspacePermissionsProvider';
import { useLegacyWorkspacePermissions } from '../../../../services/workspace-permissions';
import type { Build } from '../../../../types';
import { getSpaceIdFromUrl, getWorkspaceIdFromUrl } from '../../../../utils';
import { ActionButton } from '../../../card-footer/action-button';
import { useSolutionPlan } from '../../../solution-plan-context';
import { useHandoffAction } from '../../../utils/use-handoff-action';
import { messages as commonMessages } from '../../messages';

import { messages } from './messages';
import { saveAutomation } from './save-automation';

type AutomationFooterActionsProps = { build: Build };

type AutomationFooterActionSubjectId =
	| 'automationSolutionPlan'
	| 'saveAutomation'
	| 'saveAndEnableAutomation';

type SharedAutomationFooterActionProps = AutomationFooterActionsProps & {
	canManageAutomation: boolean;
	loadingPermissions: boolean;
	permissionDeniedTooltip: string;
	workspaceId: string;
	spaceId: string;
	sendAnalytics: (args: {
		action: 'clicked' | 'failed';
		subjectId: AutomationFooterActionSubjectId;
	}) => void;
};

const BuildAutomationButton = ({
	build,
	canManageAutomation,
	loadingPermissions,
	permissionDeniedTooltip,
	sendAnalytics,
}: SharedAutomationFooterActionProps): React.JSX.Element => {
	di(Tooltip, useHandoffAction);

	const { formatMessage } = useIntl();
	const { isSubmitting, startHandoff } = useHandoffAction(build, {
		handoffPayloadDataFactory: (solutionBuildResponse) => ({
			buildId: solutionBuildResponse.buildId!,
			type: 'AUTOMATION_RULE',
			conversationId: build.conversationId,
		}),
	});

	const handleClick = () => {
		startHandoff();
		sendAnalytics({ action: 'clicked', subjectId: 'automationSolutionPlan' });
	};

	const isLoading = loadingPermissions || isSubmitting;
	const isDisabled = !canManageAutomation;
	const isFlow = fg('studio-rename-rule-to-flow');

	return (
		<Tooltip
			content={permissionDeniedTooltip}
			canAppear={() => !canManageAutomation && !loadingPermissions}
		>
			{() => (
				<ActionButton
					build={build}
					appearance="primary"
					testId="build-automation-button"
					onClick={handleClick}
					isLoading={isLoading}
					isDisabled={isDisabled}
				>
					{isFlow
						? formatMessage(messages.buildAutomationActionLabel)
						: formatMessage(messages.buildAutomationActionLabelOld)}
				</ActionButton>
			)}
		</Tooltip>
	);
};

const SaveOrPublishAutomationButton = ({
	build,
	canManageAutomation,
	loadingPermissions,
	permissionDeniedTooltip,
	workspaceId,
	spaceId,
	sendAnalytics,
}: SharedAutomationFooterActionProps): React.JSX.Element => {
	di(saveAutomation, Tooltip, useSolutionPlan);

	const [isSaving, setIsSaving] = useState(false);

	const { formatMessage } = useIntl();
	const { showFlag } = useFlags();
	const { refetchSolutionPlan } = useSolutionPlan();

	const isLoading = loadingPermissions || isSaving;
	const isDisabled = !canManageAutomation || !workspaceId || !spaceId || isSaving;

	const handleSave = async (publish: boolean) => {
		setIsSaving(true);
		const subjectId = publish ? 'saveAndEnableAutomation' : 'saveAutomation';
		sendAnalytics({ action: 'clicked', subjectId });
		try {
			await saveAutomation({ buildId: build.id, workspaceId, spaceId, publish });
			void refetchSolutionPlan();
		} catch {
			sendAnalytics({ action: 'failed', subjectId });
			showFlag({
				title: formatMessage(messages.saveAutomationErrorTitle),
				icon: <CrossCircleIcon color={token('color.icon.danger')} label="" />,
				description: formatMessage(messages.saveAutomationErrorDescription),
				isAutoDismiss: true,
			});
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<Tooltip
			content={permissionDeniedTooltip}
			canAppear={() => !canManageAutomation && !loadingPermissions}
		>
			{() => (
				<SplitButton appearance="primary" isDisabled={isDisabled}>
					<Button
						onClick={() => {
							void handleSave(true);
						}}
						isLoading={isLoading}
						testId="save-and-publish-automation-button"
					>
						{formatMessage(messages.saveAndPublishAutomationActionLabel)}
					</Button>
					<DropdownMenu<HTMLButtonElement>
						shouldRenderToParent
						placement="bottom-end"
						trigger={({ triggerRef, ...triggerProps }) => (
							<IconButton
								{...triggerProps}
								ref={triggerRef}
								icon={ChevronDownIcon}
								label={formatMessage(messages.saveOrPublishAutomationDropdownTriggerLabel)}
								testId="save-or-publish-automation-dropdown-trigger"
							/>
						)}
					>
						<DropdownItemGroup>
							<DropdownItem
								onClick={() => {
									void handleSave(false);
								}}
								testId="save-automation-dropdown-item"
							>
								{formatMessage(messages.saveAutomationActionLabel)}
							</DropdownItem>
						</DropdownItemGroup>
					</DropdownMenu>
				</SplitButton>
			)}
		</Tooltip>
	);
};

type AutomationFooterActionsInnerProps = Omit<SharedAutomationFooterActionProps, 'sendAnalytics'>;

const AutomationFooterActionsInner = ({
	build,
	canManageAutomation,
	loadingPermissions,
	permissionDeniedTooltip,
	workspaceId,
	spaceId,
}: AutomationFooterActionsInnerProps): React.JSX.Element => {
	di(useAnalytics);

	const { sendAnalyticsEvent } = useAnalytics();
	const additionalAnalyticsAttributesEnabled = fg('additional-analytics-params-for-sa');

	const sendAnalytics = useCallback(
		({
			action,
			subjectId,
		}: {
			action: 'clicked' | 'failed';
			subjectId: AutomationFooterActionSubjectId;
		}) => {
			sendAnalyticsEvent({
				eventType: action === 'failed' ? 'operational' : 'ui',
				action,
				actionSubject: 'button',
				actionSubjectId: subjectId,
				attributes: {
					buildType: build.type,
					buildNumber: build.number,
					buildState: build.state,
					solutionId: build.solutionId,
					solutionVersion: build.solutionVersion,
					conversationId: build.conversationId,
					...(additionalAnalyticsAttributesEnabled && { draftBuildId: build.id }),
				},
			});
		},
		[
			additionalAnalyticsAttributesEnabled,
			build.conversationId,
			build.id,
			build.number,
			build.solutionId,
			build.solutionVersion,
			build.state,
			build.type,
			sendAnalyticsEvent,
		],
	);

	if (build.state === 'IN_PROGRESS' && fg('sltns-1051-save-automation-button')) {
		return (
			<SaveOrPublishAutomationButton
				build={build}
				canManageAutomation={canManageAutomation}
				loadingPermissions={loadingPermissions}
				permissionDeniedTooltip={permissionDeniedTooltip}
				workspaceId={workspaceId}
				spaceId={spaceId}
				sendAnalytics={sendAnalytics}
			/>
		);
	}

	return (
		<BuildAutomationButton
			build={build}
			canManageAutomation={canManageAutomation}
			loadingPermissions={loadingPermissions}
			permissionDeniedTooltip={permissionDeniedTooltip}
			workspaceId={workspaceId}
			spaceId={spaceId}
			sendAnalytics={sendAnalytics}
		/>
	);
};

const AutomationFooterActionsLegacy = ({
	build,
}: AutomationFooterActionsProps): React.JSX.Element => {
	di(getWorkspaceIdFromUrl, useLegacyWorkspacePermissions, useSolutionPlan);

	const { formatMessage } = useIntl();
	const workspaceIdFromUrl = useMemo(getWorkspaceIdFromUrl, []) || '';
	const spaceIdFromUrl = useMemo(getSpaceIdFromUrl, []) || '';
	const { loading: loadingPermissions, permissions } =
		useLegacyWorkspacePermissions(workspaceIdFromUrl);

	return (
		<AutomationFooterActionsInner
			build={build}
			canManageAutomation={permissions.manageAutomation}
			loadingPermissions={loadingPermissions}
			permissionDeniedTooltip={formatMessage(commonMessages.permissionDeniedTooltip)}
			workspaceId={workspaceIdFromUrl}
			spaceId={spaceIdFromUrl}
		/>
	);
};

const AutomationFooterActionsWithQuery = ({
	build,
}: AutomationFooterActionsProps): React.JSX.Element => {
	di(useSolutionPlan, WorkspacePermissionsProvider);

	const { formatMessage } = useIntl();
	const { workspaceId, spaceId, aaid } = useSolutionPlan();

	return (
		<WorkspacePermissionsProvider
			workspaceId={workspaceId || ''}
			spaceId={spaceId || ''}
			accountId={aaid}
		>
			{({ permissions }) => (
				<AutomationFooterActionsInner
					build={build}
					canManageAutomation={permissions.manageAutomation}
					loadingPermissions={false}
					permissionDeniedTooltip={formatMessage(commonMessages.permissionDeniedTooltip)}
					workspaceId={workspaceId || ''}
					spaceId={spaceId || ''}
				/>
			)}
		</WorkspacePermissionsProvider>
	);
};

const AutomationFooterActionsLoading = ({
	build,
}: AutomationFooterActionsProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<AutomationFooterActionsInner
			build={build}
			canManageAutomation={false}
			loadingPermissions
			permissionDeniedTooltip={formatMessage(commonMessages.permissionDeniedTooltip)}
			workspaceId=""
			spaceId=""
		/>
	);
};

export const AutomationFooterActions = ({
	build,
}: AutomationFooterActionsProps): React.JSX.Element => {
	// one flag is dependent on the other and the original flag is in RTs whereas the latter isn't
	// eslint-disable-next-line @atlaskit/platform/no-preconditioning
	return fg('fe_enable_sa_externally') && fg('sltns-1069-popup-blocked-bug-fix') ? (
		<Suspense fallback={<AutomationFooterActionsLoading build={build} />}>
			<AutomationFooterActionsWithQuery build={build} />
		</Suspense>
	) : (
		<AutomationFooterActionsLegacy build={build} />
	);
};
